import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

const TEMPLATE_HTML = `
<!DOCTYPE html>
<html lang="en">
<head>
    <meta charset="UTF-8">
    <meta name="viewport" content="width=device-width, initial-scale=1.0">
    <title>Changelog Step</title>
    <style>
        :root {
            --primary-color: #1890ff;
            --text-color: #333;
            --bg-color: #ffffff;
            --secondary-text: #666;
            --border-radius: 12px;
            --spacing-unit: 16px;
        }

        * {
            box-sizing: border-box;
            margin: 0;
            padding: 0;
        }

        body {
            font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
            color: var(--text-color);
            line-height: 1.6;
            background-color: var(--bg-color);
            padding: var(--spacing-unit);
            display: flex;
            flex-direction: column;
            align-items: center; 
            min-height: auto; /* Changed from 100vh to auto to prevent forced scroll */
            overflow: hidden; /* Hide scrollbars inside */
        }

        .container {
            width: 100%;
            max-width: 600px;
            margin: 0 auto;
        }

        .header {
            text-align: center;
            margin-bottom: calc(var(--spacing-unit) * 1.5);
        }

        h1 {
            font-size: 1.5rem;
            color: var(--text-color);
            margin-bottom: 0.5rem;
            font-weight: 600;
        }

        .image-container {
            width: 100%;
            border-radius: var(--border-radius);
            overflow: hidden;
            box-shadow: 0 4px 12px rgba(0,0,0,0.08);
            margin-bottom: calc(var(--spacing-unit) * 1.5);
            background: #f5f5f5;
            display: flex;
            justify-content: center;
            align-items: center;
        }

        .image-container img {
            width: 100%;
            height: auto;
            display: block;
            object-fit: cover;
        }

        .content {
            font-size: 1rem;
            color: var(--secondary-text);
            text-align: left;
        }

        .content ul {
            list-style-type: none;
            padding-left: 0;
            margin-top: 1rem;
        }

        .content li {
            position: relative;
            padding-left: 1.5rem;
            margin-bottom: 0.5rem;
        }

        .content li::before {
            content: "•";
            color: var(--primary-color);
            font-weight: bold;
            position: absolute;
            left: 0;
            font-size: 1.2em;
            line-height: 1;
            top: 2px;
        }

        @media (max-width: 480px) {
            body {
                padding: 12px;
            }
            h1 {
                font-size: 1.3rem;
            }
            .content {
                font-size: 0.95rem;
            }
        }
    </style>
    <script>
        document.addEventListener('keydown', (e) => {
            if (window.parent) {
                window.parent.postMessage({
                    type: 'CHANGELOG_KEYDOWN',
                    key: e.key,
                    code: e.code
                }, '*');
            }
        });
    </script>
</head>
<body>
    <div class="container">
        {{CONTENT}}
    </div>
</body>
</html>
`;

export default function ChangelogFrame({ version, file, title, onLoad, loading, setLoading }) {
    const { t } = useTranslation();
    const [htmlContent, setHtmlContent] = useState('');
    const [iframeHeight, setIframeHeight] = useState(0);
    const iframeRef = React.useRef(null);

    const updateHeight = () => {
        if (iframeRef.current && iframeRef.current.contentWindow && iframeRef.current.contentWindow.document && iframeRef.current.contentWindow.document.body) {
            const height = iframeRef.current.contentWindow.document.body.scrollHeight;
            if (height > 0) {
                setIframeHeight(height + 20); // Add buffer
            }
        }
    };

    useEffect(() => {
        let isMounted = true;
        setLoading(true);

        const fetchContent = async () => {
            try {
                const response = await fetch(`/changelogs/${version}/${file}`);
                if (!response.ok) throw new Error('Failed to load HTML');
                let partialContent = await response.text();

                // 1. If content is a full HTML document, extract body content or use as is (fallback)
                // Heuristic: check for <body> or <html> tags
                if (partialContent.includes('<html') || partialContent.includes('<body')) {
                    // Try to extract body content if possible, or just use it (legacy support)
                    // Simple regex to extract body content
                    const bodyMatch = partialContent.match(/<body[^>]*>([\s\S]*)<\/body>/i);
                    if (bodyMatch && bodyMatch[1]) {
                        partialContent = bodyMatch[1];
                    } else {
                        // Fallback for full html without clear body
                    }
                }

                // 2. Wrap via Template
                let fullHtml = TEMPLATE_HTML.replace('{{CONTENT}}', partialContent);

                // 3. Inject Base Tag
                const baseTag = `<base href="/changelogs/${version}/" />`;
                fullHtml = fullHtml.replace('<head>', `<head>${baseTag}`);

                // 4. Handle Translations
                fullHtml = fullHtml.replace(/\{\{\s*t\('([^']+)'\)\s*\}\}/g, (match, key) => {
                    return t(key) || key;
                });
                fullHtml = fullHtml.replace(/\{\{\s*t\("([^"]+)"\)\s*\}\}/g, (match, key) => {
                    return t(key) || key;
                });

                if (isMounted) {
                    setHtmlContent(fullHtml);
                    setLoading(false);
                    if (onLoad) onLoad();
                    // Give a small delay for render to settle before height calculation
                    setTimeout(updateHeight, 100);
                }
            } catch (error) {
                console.error("Error loading changelog frame:", error);
                if (isMounted) setLoading(false);
            }
        };

        fetchContent();

        return () => {
            isMounted = false;
        };
    }, [version, file, t, setLoading, onLoad]);

    return (
        <iframe
            ref={iframeRef}
            srcDoc={htmlContent}
            style={{ width: '100%', height: iframeHeight ? `${iframeHeight}px` : '100%', border: 'none', display: loading ? 'none' : 'block', overflow: 'hidden' }}
            title={`Changelog ${version} - ${title}`}
            sandbox="allow-same-origin allow-scripts"
            onLoad={updateHeight}
        />
    );
}
